import React, { useEffect, useState, useRef } from "react";
import styles from "./RatingListing.module.css";
import StarRatings from "react-star-ratings";
import { Progress } from "antd";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";

export const formatDate = (isoDate) => {
  const date = new Date(isoDate);
  const day = date.getUTCDate();
  const month = date.toLocaleString("default", { month: "long" });
  const year = date.getUTCFullYear();
  return `${day} ${month} ${year}`;
};

const RatingListing = ({ reviews, details }) => {
  /* ----------- animated progress bars ----------- */
  const [animatedValues, setAnimatedValues] = useState({
    one: 0,
    two: 0,
    three: 0,
    four: 0,
    five: 0,
  });
  const ratings = { ...details?.ratings[0] };
  delete ratings._id;
  const totalRatings = Object.values(ratings).reduce((s, v) => s + v, 0);

  /* ----------- swiper ref for arrows ----------- */
  const swiperRef = useRef(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setAnimatedValues((prev) => {
        const next = { ...prev };
        let done = true;
        Object.keys(ratings).forEach((k) => {
          const target = Math.round((ratings[k] / totalRatings) * 100);
          if (next[k] < target) {
            next[k] = Math.min(next[k] + 5, target);
            done = false;
          }
        });
        if (done) clearInterval(interval);
        return next;
      });
    }, 30);
    return () => clearInterval(interval);
  }, [ratings, totalRatings]);

  return (
    <>
    <h2 className={styles.testTitle}>Testimonials</h2>
    <div className={styles.outer}>
      
      {/* -------- Left: score breakdown -------- */}
      <div className={styles.left}>
        <div className={styles.rateModal}>
          <h5 className={styles.customerReviews}>Customer Reviews</h5>
          <div className={styles.rateTitle}>
            <StarRatings
              rating={details?.ratingAvg}
              starDimension="16px"
              starSpacing="3px"
              starRatedColor="#FFD700"
              numberOfStars={5}
              name="rating"
            />
            <h3>{details?.ratingAvg?.toFixed(2)} out of 5</h3>
          </div>
          <div className={styles.rateCounts}>
            {details?.reviewCount ?? 0} ratings
          </div>

          {Object.entries(ratings)
            .reverse()
            .map(([key, value], idx) => {
              const rating = 5 - idx;
              return (
                <div className={styles.progressFlex} key={rating}>
                  <div className={styles.ratingLine}>
                    <p>{rating}</p>
                    <p>Star</p>
                  </div>
                  <Progress
                    percent={animatedValues[key]}
                    showInfo
                    strokeWidth={14}
                    strokeLinecap="square"
                    strokeColor="#ff6666"
                    format={(p) => <span>{p}%</span>}
                  />
                </div>
              );
            })}
        </div>
      </div>

      {/* -------- Right: reviews carousel -------- */}
      <div className={styles.right}>
        {/* Swiper arrows */}
        <img
          src="/img/left-arrow.svg"
          alt="prev"
          className={styles.arrowLeft}
          onClick={() => swiperRef.current?.slidePrev()}
        />
        <img
          src="/img/right-arrow.svg"
          alt="next"
          className={styles.arrowRight}
          onClick={() => swiperRef.current?.slideNext()}
        />

        <Swiper
          modules={[Navigation, Autoplay]}
          autoplay={{ delay: 5000, disableOnInteraction: false }}
          loop
          onSwiper={(swiper) => (swiperRef.current = swiper)}
          className={styles.reviewSwiper}
        >
          {reviews?.map((review) => (
            <SwiperSlide key={review._id || review.id}>
              <div className={styles.reviewCard}>
                <div className={styles.reviewHeader}>
                  <img
                    src="https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_960_720.png"
                    alt={review.customerName}
                    className={styles.profileImage}
                  />
                  <h3 className={styles.reviewerName}>{review.customerName}</h3>
                </div>

                <StarRatings
                  rating={review.rating}
                  starDimension="16px"
                  starSpacing="3px"
                  starRatedColor="#FFD700"
                  numberOfStars={5}
                  name="rating"
                />

                <p className={styles.reviewDate}>
                  {formatDate(review.createdAt)}
                </p>
                <p className={styles.reviewText}>{review.review}</p>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
    </>
  );
};

export default RatingListing;
